import React, { useState, useEffect, useRef } from 'react';
import './Gallery.css';

// Import all images from the gallery folder
import image1 from '../../assets/gallery/1.png';
import image2 from '../../assets/gallery/2.png';
import image3 from '../../assets/gallery/3.png';
import image4 from '../../assets/gallery/4.png';
import image5 from '../../assets/gallery/5.png';
import image6 from '../../assets/gallery/6.png';
import image7 from '../../assets/gallery/7.png';
import image8 from '../../assets/gallery/8.png';
import image9 from '../../assets/gallery/9.png';
import image10 from '../../assets/gallery/10.png';
import image11 from '../../assets/gallery/11.png';
import image12 from '../../assets/gallery/12.png';
import image13 from '../../assets/gallery/13.png';
import image14 from '../../assets/gallery/14.png';

const Gallery: React.FC = () => {
  const images = [
    { src: image1, alt: 'Game Screenshot 1' },
    { src: image2, alt: 'Game Screenshot 2' },
    { src: image3, alt: 'Game Screenshot 3' },
    { src: image4, alt: 'Game Screenshot 4' },
    { src: image5, alt: 'Game Screenshot 5' },
    { src: image6, alt: 'Game Screenshot 6' },
    { src: image7, alt: 'Game Screenshot 7' },
    { src: image8, alt: 'Game Screenshot 8' },
    { src: image9, alt: 'Game Screenshot 9' },
    { src: image10, alt: 'Game Screenshot 10' },
    { src: image11, alt: 'Game Screenshot 11' },
    { src: image12, alt: 'Game Screenshot 12' },
    { src: image13, alt: 'Game Screenshot 13' },
    { src: image14, alt: 'Game Screenshot 14' },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState('');
  const [fullScreenIndex, setFullScreenIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Function to handle next slide
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  // Function to handle previous slide
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  // Function to open full screen image
  const openFullScreen = (src: string, index: number) => {
    setFullScreenImage(src);
    setFullScreenIndex(index);
    setIsFullScreen(true);
    setIsPaused(true);
  };

  // Function to close full screen image
  const closeFullScreen = () => {
    setIsFullScreen(false);
    setIsPaused(false);
  };

  // Function to navigate to next image in fullscreen
  const nextFullScreenImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextIndex = (fullScreenIndex + 1) % images.length;
    setFullScreenImage(images[nextIndex].src);
    setFullScreenIndex(nextIndex);
  };

  // Function to navigate to previous image in fullscreen
  const prevFullScreenImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    const prevIndex = (fullScreenIndex - 1 + images.length) % images.length;
    setFullScreenImage(images[prevIndex].src);
    setFullScreenIndex(prevIndex);
  };

  // Auto-scroll functionality
  useEffect(() => {
    if (!isPaused) {
      autoScrollInterval.current = setInterval(() => {
        nextSlide();
      }, 6000); // Slow cycling - 6 seconds per image
    }

    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [isPaused]);

  // Pause auto-scroll when hovering over carousel
  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (!isFullScreen) {
      setIsPaused(false);
    }
  };

  // Calculate transform value based on device type
  const getTransformValue = () => {
    if (isMobile) {
      return `translateX(-${currentIndex * 100}%)`;
    } else if (window.innerWidth <= 1024) {
      return `translateX(-${currentIndex * 50}%)`;
    } else {
      return `translateX(-${currentIndex * (100 / 3)}%)`;
    }
  };

  return (
    <section id="gallery" className="gallery-section">
      <h2 className="gallery-title">Gallery</h2>
      
      {/* Image Carousel */}
      <div 
        className="carousel-container" 
        ref={carouselRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="carousel-track" style={{ transform: getTransformValue() }}>
          {images.map((image, index) => (
            <div 
              key={index} 
              className="carousel-item"
              onClick={() => openFullScreen(image.src, index)}
            >
              <img src={image.src} alt={image.alt} />
            </div>
          ))}
        </div>
        
        <button className="carousel-button prev" onClick={prevSlide}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        
        <button className="carousel-button next" onClick={nextSlide}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      
      {/* Full Screen Image Modal */}
      {isFullScreen && (
        <div className="fullscreen-modal" onClick={closeFullScreen}>
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <img src={fullScreenImage} alt="Full screen view" />
            
            <button className="close-button" onClick={closeFullScreen}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            {/* Navigation arrows for fullscreen view */}
            <button className="fullscreen-nav-button prev" onClick={prevFullScreenImage}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <button className="fullscreen-nav-button next" onClick={nextFullScreenImage}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
